import type {
  CmmdProjectRepository,
  CmmdTaskStatus,
  EnvironmentId,
  ProjectId,
  ThreadId,
} from "@cmmd-forge/contracts";
export {
  applyCommandCenterFilters,
  buildCommandCenterAssigneeOptions,
  buildCommandCenterCampaignOptions,
  buildCommandCenterProjectOptions,
  buildCommandCenterTeamOptions,
  hasActiveCommandCenterFilters,
  isCommandCenterCmmdAuthRequiredError,
  matchesCommandCenterSearch,
  resolveCommandCenterEmptyStateContent,
  resolveCommandCenterFreshnessIndicator,
  scopeCommandCenterItemsByCampaign,
  scopeCommandCenterItemsByProject,
  scopeCommandCenterItemsByTeam,
  scopeCommandCenterItemsByWorkspace,
  scopeCommandCenterProjectsByCampaign,
  scopeCommandCenterProjectsByTeam,
  scopeCommandCenterProjectsByWorkspace,
  scopeCommandCenterTasksByCampaign,
  scopeCommandCenterTasksByProject,
  scopeCommandCenterTasksByTeam,
  scopeCommandCenterTasksByWorkspace,
  shouldRenderCommandCenterErrorBanner,
  shouldRenderCommandCenterFreshnessIndicator,
} from "./NoActiveThreadState.filters";

export {
  buildNoActiveThreadCommandTaskRows,
  buildNoActiveThreadSnapshot,
} from "./NoActiveThreadState.snapshots";

export {
  COMMAND_CENTER_ASSIGNEE_FILTER_KEY,
  COMMAND_CENTER_CAMPAIGN_FILTER_KEY,
  COMMAND_CENTER_MANUAL_TASKS_KEY,
  COMMAND_CENTER_PROJECT_FILTER_KEY,
  COMMAND_CENTER_SEARCH_QUERY_KEY,
  COMMAND_CENTER_STATUS_FILTER_KEY,
  COMMAND_CENTER_TEAM_FILTER_KEY,
  COMMAND_CENTER_VIEW_KEY,
  readStoredAssigneeFilter,
  readStoredCampaignFilter,
  readStoredManualTasks,
  readStoredProjectFilter,
  readStoredSearchQuery,
  readStoredStatusFilter,
  readStoredTeamFilter,
  readStoredView,
  writeStoredManualTasks,
} from "./NoActiveThreadState.storage";

export type CommandCenterFreshnessTone = "live" | "syncing" | "stale" | "offline" | "out-of-date";

export interface CommandCenterFreshnessIndicator {
  readonly tone: CommandCenterFreshnessTone;
  readonly label: string;
  readonly title?: string;
}

export interface NoActiveThreadSnapshotInput {
  readonly projects: ReadonlyArray<{
    readonly id: ProjectId;
    readonly environmentId: EnvironmentId;
    readonly name: string;
  }>;
  readonly threads: ReadonlyArray<{
    readonly id: ThreadId;
    readonly environmentId: EnvironmentId;
    readonly projectId: ProjectId;
    readonly title: string;
    readonly createdAt: string;
    readonly updatedAt?: string | undefined;
    readonly latestUserMessageAt: string | null;
  }>;
  readonly maxRecentThreads?: number;
}

export interface NoActiveThreadRecentItem {
  readonly id: ThreadId;
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly title: string;
  readonly projectName: string;
  readonly activityAt: string;
}

export interface NoActiveThreadSnapshot {
  readonly projectCount: number;
  readonly threadCount: number;
  readonly recentThreads: ReadonlyArray<NoActiveThreadRecentItem>;
}

export interface NoActiveThreadCommandTaskSnapshotInput {
  readonly campaigns: ReadonlyArray<{
    readonly id: string;
    readonly title: string;
  }>;
  readonly projects: ReadonlyArray<{
    readonly id: string;
    readonly title: string;
    /** Forge display name; null falls back to `title`. Chrome only, never a join key. */
    readonly shortName?: string | null;
    readonly campaignId: string | null;
    readonly workspaceId?: string | null;
    readonly workspaceName?: string | null;
    readonly workspaceIconUrl?: string | null;
    readonly icon?: string | null;
    readonly iconUrl?: string | null;
    readonly teamId?: string | null;
    readonly teamName?: string | null;
  }>;
  readonly tasks: ReadonlyArray<{
    readonly id: string;
    readonly title: string;
    readonly status: string | null;
    readonly priority: string | null;
    readonly projectId: string | null;
    readonly dueDate: string | null;
    readonly updatedAt: string | null;
    readonly workspaceId?: string | null;
    readonly workspaceName?: string | null;
    readonly workspaceIconUrl?: string | null;
    readonly teamId?: string | null;
    readonly teamName?: string | null;
    readonly assigneeId?: string | null;
    readonly dependsOn: ReadonlyArray<string>;
  }>;
  readonly maxTasks?: number;
}

export interface NoActiveThreadCommandTaskRow {
  readonly id: string;
  readonly title: string;
  readonly status: string | null;
  readonly priority: string | null;
  readonly projectId: string | null;
  readonly projectName: string;
  readonly projectIcon?: string | null;
  readonly projectIconUrl?: string | null;
  readonly campaignName: string | null;
  readonly workspaceId: string | null;
  readonly workspaceName: string | null;
  readonly workspaceIconUrl?: string | null;
  readonly teamId: string | null;
  readonly teamName: string | null;
  readonly assigneeId: string | null;
  readonly dueDate: string | null;
  readonly updatedAt: string | null;
  /** Dependency task ids; empty array when the task has no blockers. */
  readonly dependsOn: ReadonlyArray<string>;
}

export interface CommandCenterFilterableItem {
  readonly id: string;
  readonly source: string;
  readonly title?: string;
  readonly status: string | null;
  readonly priority?: string | null;
  readonly projectName?: string;
  readonly campaignName?: string | null;
  readonly workspaceName?: string | null;
  readonly teamName?: string | null;
  readonly agentStatus: string;
  readonly isReady: boolean;
  readonly workspaceId: string | null;
  readonly teamId: string | null;
  readonly environmentId: string | null;
}

export interface CommandCenterTaskMeta {
  readonly workspaceId: string | null;
  readonly teamId: string | null;
  readonly campaignId: string | null;
  readonly projectId: string | null;
}

export interface CommandCenterOption {
  readonly value: string;
  readonly label: string;
}

export interface CommandCenterScopedItem {
  readonly id: string;
  readonly source: string;
  readonly workspaceId: string | null;
  readonly teamId: string | null;
  readonly environmentId: string | null;
}

export interface CommandCenterScopedTask {
  readonly id: string;
  readonly projectId?: string | null;
  readonly campaignId?: string | null;
  readonly teamName?: string | null;
  readonly assigneeId?: string | null;
}

export interface CommandCenterScopedProject {
  readonly id: string;
  /** Board name of record — what `resolveImportedCmmdProjectRef` joins on. */
  readonly title: string;
  /** Forge display name; null falls back to `title`. Chrome only. */
  readonly shortName?: string | null;
  readonly workspaceId?: string | null;
  readonly teamId?: string | null;
  readonly teamName?: string | null;
  readonly campaignId?: string | null;
}

export interface CommandCenterScopedCampaign {
  readonly id: string;
  readonly title: string;
}

export interface CommandCenterFilters {
  readonly activeWorkspaceId: string | null;
  readonly searchQuery: string;
  readonly teamFilter: string;
  readonly campaignFilter: string;
  readonly projectFilter: string;
  readonly statusFilter: CmmdTaskStatus | "all";
  readonly assigneeFilter: string;
  readonly readyOnly: boolean;
}

export interface CommandCenterActiveFilterInput {
  readonly activeWorkspaceId: string | null;
  readonly teamFilter: string;
  readonly campaignFilter: string;
  readonly projectFilter: string;
  readonly statusFilter: CmmdTaskStatus | "all";
  readonly assigneeFilter: string;
  readonly searchQuery: string;
  readonly readyOnly: boolean;
}

export interface CommandCenterEmptyStateContent {
  readonly title: string;
  readonly description: string;
  readonly actionLabel: string;
}

export interface ManualCommandTask {
  readonly id: string;
  readonly title: string;
  readonly status: CmmdTaskStatus;
  readonly projectEnvironmentId: EnvironmentId | null;
  readonly projectId: ProjectId | null;
  readonly projectName: string | null;
  readonly cmmdProjectId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CmmdTaskMetadataSource {
  readonly id: string;
  readonly workspaceId?: string | null;
  readonly projectId?: string | null;
  readonly campaignId?: string | null;
  readonly teamId?: string | null;
  readonly title?: string;
  readonly assigneeId?: string | null;
}

export interface CmmdProjectMetadataSource {
  readonly id: string;
  readonly workspaceId?: string | null;
  readonly teamId?: string | null;
  readonly campaignId?: string | null;
}

export interface CmmdCampaignMetadataSource {
  readonly id: string;
}

export function buildCmmdTaskByWorkItemId<T extends { readonly id: string }>(
  tasks: readonly T[],
): Map<string, T> {
  const map = new Map<string, T>();
  for (const task of tasks) {
    map.set(`cmmd:${task.id}`, task);
  }
  return map;
}

export function buildCmmdTaskTitleById(
  tasks: readonly { readonly id: string; readonly title: string }[],
): Map<string, string> {
  return new Map(tasks.map((task) => [task.id, task.title] as const));
}

export function buildAssigneeByWorkItemId(
  tasks: readonly { readonly id: string; readonly assigneeId?: string | null }[],
): Map<string, string | null> {
  const map = new Map<string, string | null>();
  for (const task of tasks) {
    map.set(`cmmd:${task.id}`, task.assigneeId ?? null);
  }
  return map;
}

export function buildCmmdTaskMetaByWorkItemId(input: {
  readonly tasks: readonly CmmdTaskMetadataSource[];
  readonly projects: readonly CmmdProjectMetadataSource[];
  readonly campaigns: readonly CmmdCampaignMetadataSource[];
  readonly projectById: ReadonlyMap<string, CmmdProjectMetadataSource>;
}): Map<string, CommandCenterTaskMeta> {
  const map = new Map<string, CommandCenterTaskMeta>();
  for (const task of input.tasks) {
    const project = task.projectId ? (input.projectById.get(task.projectId) ?? null) : null;
    map.set(`cmmd:${task.id}`, {
      workspaceId: task.workspaceId ?? project?.workspaceId ?? null,
      teamId: task.teamId ?? project?.teamId ?? null,
      campaignId: task.campaignId ?? project?.campaignId ?? null,
      projectId: task.projectId ?? null,
    });
  }
  for (const project of input.projects) {
    map.set(`cmmd-project:${project.id}`, {
      workspaceId: project.workspaceId ?? null,
      teamId: project.teamId ?? null,
      campaignId: project.campaignId ?? null,
      projectId: project.id,
    });
  }
  for (const campaign of input.campaigns) {
    const linkedProjects = input.projects.filter((project) => project.campaignId === campaign.id);
    const workspaceIds = new Set(
      linkedProjects
        .map((project) => project.workspaceId ?? null)
        .filter((value): value is string => value !== null),
    );
    map.set(`cmmd-campaign:${campaign.id}`, {
      workspaceId: workspaceIds.size === 1 ? (workspaceIds.values().next().value as string) : null,
      teamId: null,
      campaignId: campaign.id,
      projectId: null,
    });
  }
  return map;
}

export function buildCommandCenterWorkspaceOptions(input: {
  readonly workspaces: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly fallbackWorkspace: { readonly id: string; readonly name: string } | null | undefined;
}): Array<{ readonly id: string; readonly name: string }> {
  if (input.workspaces.length > 0) {
    return input.workspaces.map((item) => ({ id: item.id, name: item.name }));
  }
  return input.fallbackWorkspace
    ? [{ id: input.fallbackWorkspace.id, name: input.fallbackWorkspace.name }]
    : [];
}

export function buildProjectNameByScopedId(
  projects: ReadonlyArray<{
    readonly environmentId: EnvironmentId;
    readonly id: ProjectId;
    readonly name: string;
  }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const project of projects) {
    map.set(`${project.environmentId}:${project.id}`, project.name);
  }
  return map;
}

export interface CommandCenterThreadSummaryInput {
  readonly id: ThreadId;
  readonly title: string;
  readonly status: string;
  readonly session?: { readonly provider?: string | null } | null;
  readonly createdAt?: string | null | undefined;
  readonly updatedAt?: string | null | undefined;
  readonly latestUserMessageAt: string | null;
  readonly anchorKind: string;
  readonly anchorCmmdId: string | null;
}

export function buildSelectedTaskThreadSummaries(input: {
  readonly selectedItemId: string | null;
  readonly environmentStateById: Readonly<
    Record<
      string,
      {
        readonly sidebarThreadSummaryById: Readonly<
          Record<string, CommandCenterThreadSummaryInput>
        >;
      }
    >
  >;
}): readonly {
  readonly id: ThreadId;
  readonly environmentId: EnvironmentId;
  readonly title: string;
  readonly status: string;
  readonly provider: string | null;
  readonly updatedAt: string | null;
  readonly latestUserMessageAt: string | null;
}[] {
  if (!input.selectedItemId?.startsWith("cmmd:")) return [];
  const rawTaskId = input.selectedItemId.slice("cmmd:".length);
  const threads: {
    id: ThreadId;
    environmentId: EnvironmentId;
    title: string;
    status: string;
    provider: string | null;
    updatedAt: string | null;
    latestUserMessageAt: string | null;
  }[] = [];

  for (const [environmentId, environmentState] of Object.entries(input.environmentStateById)) {
    for (const thread of Object.values(environmentState.sidebarThreadSummaryById)) {
      if (thread.anchorKind !== "task") continue;
      if (!taskThreadAnchorMatches(thread.anchorCmmdId, rawTaskId)) continue;
      threads.push({
        id: thread.id,
        environmentId: environmentId as EnvironmentId,
        title: thread.title,
        status: thread.status,
        provider: thread.session?.provider ?? null,
        updatedAt: thread.updatedAt ?? thread.createdAt ?? null,
        latestUserMessageAt: thread.latestUserMessageAt,
      });
    }
  }

  return threads.toSorted((left, right) => {
    const leftTime = Date.parse(left.latestUserMessageAt ?? left.updatedAt ?? "");
    const rightTime = Date.parse(right.latestUserMessageAt ?? right.updatedAt ?? "");
    return (
      (Number.isFinite(rightTime) ? rightTime : 0) - (Number.isFinite(leftTime) ? leftTime : 0)
    );
  });
}

export function buildMissingForgeProjectPrompt(input: {
  readonly itemId: string;
  readonly payloadTitle: string;
  readonly taskById: ReadonlyMap<string, { readonly projectId?: string | null }>;
  readonly projectById: ReadonlyMap<string, { readonly id: string; readonly title: string }>;
}): {
  readonly itemId: string;
  readonly taskTitle: string;
  readonly cmmdProjectId: string;
  readonly cmmdProjectTitle: string;
} | null {
  const rawId = input.itemId.startsWith("cmmd:") ? input.itemId.slice(5) : input.itemId;
  const cmmdTask = input.taskById.get(rawId);
  if (!cmmdTask?.projectId) return null;
  const cmmdProject = input.projectById.get(cmmdTask.projectId);
  if (!cmmdProject) return null;
  return {
    itemId: input.itemId,
    taskTitle: input.payloadTitle,
    cmmdProjectId: cmmdProject.id,
    cmmdProjectTitle: cmmdProject.title,
  };
}

export function resolveImportedCmmdProjectRef(input: {
  readonly itemId: string;
  readonly taskById: ReadonlyMap<string, { readonly projectId?: string | null }>;
  readonly projectById: ReadonlyMap<string, { readonly id: string; readonly title: string }>;
  readonly forgeProjects: ReadonlyArray<{
    readonly environmentId: EnvironmentId;
    readonly id: ProjectId;
    readonly name: string;
  }>;
}): { readonly environmentId: EnvironmentId; readonly projectId: ProjectId } | null {
  const rawId = input.itemId.startsWith("cmmd:") ? input.itemId.slice(5) : input.itemId;
  const cmmdTask = input.taskById.get(rawId);
  if (!cmmdTask?.projectId) return null;
  const cmmdProject = input.projectById.get(cmmdTask.projectId);
  if (!cmmdProject) return null;
  const importedProject =
    input.forgeProjects.find((project) => project.name.trim() === cmmdProject.title.trim()) ?? null;
  return importedProject
    ? { environmentId: importedProject.environmentId, projectId: importedProject.id }
    : null;
}

export function sanitizeCmmdTaskSyncError(error: unknown): string {
  if (!(error instanceof Error)) {
    return "CMMD did not confirm the task update. Retry sync from Projects.";
  }
  const message = error.message.trim();
  if (
    /\b(HTTP\s*)?5\d\d\b/i.test(message) ||
    /Failed to update CMMD task/i.test(message) ||
    /provider|api key|secret|token|upstream/i.test(message)
  ) {
    return "CMMD did not confirm the task update. Retry sync from Projects.";
  }
  return message || "CMMD did not confirm the task update. Retry sync from Projects.";
}

export async function readHttpErrorMessage(response: Response, fallback: string): Promise<string> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/json")) {
    return fallback;
  }

  const body = (await response.json().catch(() => null)) as { error?: unknown } | null;
  return typeof body?.error === "string" && body.error.trim() ? body.error : fallback;
}

export function resolveIdentityDisplayName(
  identity: {
    readonly firstName: string | null;
    readonly lastName: string | null;
    readonly username: string;
    readonly email: string;
  } | null,
): string | null {
  if (!identity) return null;
  const fullName = [identity.firstName, identity.lastName]
    .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
    .join(" ")
    .trim();
  if (fullName.length > 0) return fullName;
  if (identity.username.trim().length > 0) return identity.username.trim();
  if (identity.email.trim().length > 0) return identity.email.trim();
  return null;
}

function normalizeTaskThreadAnchorId(value: string): string {
  const trimmed = value.trim().toLowerCase();
  const withoutCmmdPrefix = trimmed.startsWith("cmmd:") ? trimmed.slice("cmmd:".length) : trimmed;
  const withoutTaskPrefix = withoutCmmdPrefix.replace(/^(task[-_:]|t[-_:])/, "");
  return withoutTaskPrefix.replace(/^0+(?=\d)/, "");
}

export function taskThreadAnchorMatches(anchorCmmdId: string | null, rawTaskId: string): boolean {
  if (!anchorCmmdId) return false;
  return normalizeTaskThreadAnchorId(anchorCmmdId) === normalizeTaskThreadAnchorId(rawTaskId);
}

export function resolveProjectRefFromRepoState<E extends string, P extends string>(input: {
  readonly repoResult: CmmdProjectRepository;
  readonly projects: readonly { readonly environmentId: E; readonly id: P }[];
}): { readonly environmentId: E; readonly projectId: P } | null {
  const { repoResult, projects } = input;
  if (repoResult.repoState !== "repo_linked_forge_linked" || !repoResult.forgeLink) return null;
  const { forgeEnvironmentId, forgeProjectId } = repoResult.forgeLink;
  const match = projects.find(
    (p) => p.environmentId === forgeEnvironmentId && p.id === forgeProjectId,
  );
  if (!match) return null;
  return { environmentId: match.environmentId, projectId: match.id };
}

export function shouldAutoStartTaskThread(input: {
  readonly cmmdTaskId: string | null;
  readonly targetProject: { readonly repositoryIdentity?: unknown | null } | null;
  readonly importedFromCmmdProject: boolean;
}): boolean {
  if (!input.cmmdTaskId) return true;
  if (!input.importedFromCmmdProject) return true;
  return input.targetProject?.repositoryIdentity != null;
}
